import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InboxIcon } from '@/components/catalog/icons';
import {
  NotificationLogList,
  type LogUser,
} from '@/components/notifications/admin/NotificationLogList';
import {
  DECISIONS,
  OUTCOMES,
  flattenCatalogue,
  isDecision,
} from '@/components/notifications/admin/log-labels';
import {
  Button,
  EmptyState,
  Field,
  Input,
  ListPager,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelHeader,
  type MultiComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import type {
  NotificationCatalogue,
  NotificationLogParams,
  NotificationLogRow,
  Paginated,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * The notification send log — GET /notifications/log (`f_notification_deliveries`,
 * kept forever). Read-only. Every filter lives in the URL (`eventKey`, `userId`,
 * `decision`, `from`, `to`, `page`); a default is an absent key.
 */

const PER_PAGE = 25;
/** The server's `perPage` ceiling; "Show all" asks for one page of this size. */
const SHOW_ALL_PER_PAGE = 100;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const DENIED = 'Your permission set does not include this. An administrator can change that.';

/*
 * `eventKey`, `userId` and `decision` are multi-value (owner, 2026-10-28): each
 * is a comma-separated list in ONE URL param, sent to the API as the same CSV.
 * A single-value bookmark parses as a one-item list; absent = all.
 */
interface ParsedQuery {
  eventKeys: string[];
  userIds: number[];
  decisions: string[];
  from: string;
  to: string;
  page: number;
}

function readList(sp: URLSearchParams, key: string): string[] {
  const parts = (sp.get(key) ?? '').split(',').map((part) => part.trim());
  return [...new Set(parts.filter((part) => part !== ''))];
}

function parseQuery(sp: URLSearchParams): ParsedQuery {
  const rawPage = Number(sp.get('page') ?? '');
  const from = sp.get('from') ?? '';
  const to = sp.get('to') ?? '';
  return {
    eventKeys: readList(sp, 'eventKey'),
    userIds: readList(sp, 'userId')
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0),
    decisions: readList(sp, 'decision').filter((decision) => isDecision(decision)),
    from: DATE_RE.test(from) ? from : '',
    to: DATE_RE.test(to) ? to : '',
    page: Number.isInteger(rawPage) && rawPage > 1 ? rawPage : 1,
  };
}

const OUTCOME_OPTIONS: MultiComboboxOption[] = DECISIONS.map((decision) => ({
  value: decision,
  label: OUTCOMES[decision].label,
}));

export function NotificationLogPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead('settings-notifications');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const query = useMemo(() => parseQuery(new URLSearchParams(queryString)), [queryString]);

  const [showAll, setShowAll] = useState(false);
  const [result, setResult] = useState<Paginated<NotificationLogRow> | null>(null);
  /** The request key the current result/error answers; loading = it differs. */
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const requestKey = `${queryString}|${showAll ? 'all' : 'paged'}`;
  const loading = loadedFor !== requestKey;
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [catalogue, setCatalogue] = useState<NotificationCatalogue | null>(null);
  const [users, setUsers] = useState<LogUser[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);

  /* Filter sources: the catalogue and the staff directory. */
  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .getNotificationCatalogue(signal)
      .then((res) => {
        if (!signal.aborted) setCatalogue(res.data);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setLookupError(caught instanceof Error ? caught.message : 'Failed to load the event list');
      });
    api
      .listAllUsers({}, signal)
      .then((res) => {
        if (!signal.aborted) {
          setUsers(res.data.map((u) => ({ id: u.id, name: u.name, email: u.email })));
        }
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setLookupError(caught instanceof Error ? caught.message : 'Failed to load people');
      });
    return () => controller.abort();
  }, [canRead]);

  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    const { signal } = controller;
    const current = parseQuery(new URLSearchParams(queryString));
    const params: NotificationLogParams = showAll
      ? { page: 1, perPage: SHOW_ALL_PER_PAGE }
      : { page: current.page, perPage: PER_PAGE };
    if (current.eventKeys.length > 0) params.eventKey = current.eventKeys.join(',');
    if (current.userIds.length > 0) params.userId = current.userIds.join(',');
    if (current.decisions.length > 0) params.decision = current.decisions.join(',');
    if (current.from) params.from = current.from;
    if (current.to) params.to = current.to;
    const key = `${queryString}|${showAll ? 'all' : 'paged'}`;
    api
      .listNotificationLog(params, signal)
      .then((res) => {
        if (signal.aborted) return;
        setResult(res);
        setError(null);
        setForbidden(false);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          setResult(null);
          setError(null);
          return;
        }
        setError(caught instanceof Error ? caught.message : 'Failed to load the send log');
      })
      .finally(() => {
        if (!signal.aborted) setLoadedFor(key);
      });
    return () => controller.abort();
  }, [canRead, queryString, showAll]);

  const applyPatch = useCallback(
    (patch: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === '') next.delete(key);
        else next.set(key, value);
      }
      if (!('page' in patch)) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const entries = useMemo(() => flattenCatalogue(catalogue), [catalogue]);
  const eventLabels = useMemo(
    () => new Map(entries.map((entry) => [entry.event.key, entry.event.label])),
    [entries],
  );
  const eventOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = entries.map((entry) => ({
      value: entry.event.key,
      label: entry.event.label,
      hint: `${entry.moduleLabel} · ${entry.submoduleLabel}`,
    }));
    for (const key of query.eventKeys) {
      if (!eventLabels.has(key)) options.unshift({ value: key, label: key });
    }
    return options;
  }, [entries, eventLabels, query.eventKeys]);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const userOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = [...users]
      .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
      .map((u) => ({ value: String(u.id), label: u.name.trim() || u.email, hint: u.email }));
    for (const id of query.userIds) {
      if (!userMap.has(id)) options.unshift({ value: String(id), label: `User #${id}` });
    }
    return options;
  }, [users, userMap, query.userIds]);

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader title="Notification log" />
        <Panel>
          <EmptyState
            icon={<InboxIcon />}
            title="Not available to your account"
            description={DENIED}
          />
        </Panel>
      </div>
    );
  }

  const rows = result?.data ?? [];
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const pageSize = showAll ? SHOW_ALL_PER_PAGE : PER_PAGE;
  const page = showAll ? 1 : query.page;
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const anyFilter =
    query.eventKeys.length > 0 ||
    query.userIds.length > 0 ||
    query.decisions.length > 0 ||
    query.from !== '' ||
    query.to !== '';
  const initialLoad = loading && !result;

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader title="Notification log" />

      <Panel className="min-w-0" aria-labelledby="notification-log-heading">
        <PanelHeader
          id="notification-log-heading"
          title="Deliveries"
          description={initialLoad ? 'Loading…' : `${total} ${total === 1 ? 'entry' : 'entries'}`}
        />

        <div className="flex flex-wrap items-end gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field label="Event" hideLabel>
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  values={query.eventKeys}
                  onChange={(next) => applyPatch({ eventKey: next.join(',') })}
                  options={eventOptions}
                  placeholder="All events"
                  searchPlaceholder="Search events…"
                  summaryNoun={{ singular: 'event', plural: 'events' }}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-56">
            <Field label="Person" hideLabel>
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  values={query.userIds.map(String)}
                  onChange={(next) => applyPatch({ userId: next.join(',') })}
                  options={userOptions}
                  placeholder="Everyone"
                  searchPlaceholder="Search people…"
                  summaryNoun={{ singular: 'person', plural: 'people' }}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-44">
            <Field label="Outcome" hideLabel>
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  values={query.decisions}
                  onChange={(next) => applyPatch({ decision: next.join(',') })}
                  options={OUTCOME_OPTIONS}
                  placeholder="All outcomes"
                  searchPlaceholder="Search outcomes…"
                  summaryNoun={{ singular: 'outcome', plural: 'outcomes' }}
                />
              )}
            </Field>
          </div>
          <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:flex sm:w-auto">
            <Field label="From" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  aria-label="From"
                  value={query.from}
                  max={query.to || undefined}
                  onChange={(e) => applyPatch({ from: e.target.value })}
                  className="sm:w-40"
                />
              )}
            </Field>
            <Field label="To" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  aria-label="To"
                  value={query.to}
                  min={query.from || undefined}
                  onChange={(e) => applyPatch({ to: e.target.value })}
                  className="sm:w-40"
                />
              )}
            </Field>
          </div>
          {anyFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>

        {lookupError && (
          <Notice tone="warning" className="m-4">
            <span className="[overflow-wrap:anywhere]">{lookupError}</span>
          </Notice>
        )}

        {forbidden && (
          <Notice tone="danger" role="alert" className="m-4">
            {DENIED}
          </Notice>
        )}

        {error && (
          <Notice tone="danger" role="alert" className="m-4" title={<>Couldn&rsquo;t load the send log.</>}>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        {!forbidden && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            {!initialLoad && rows.length === 0 ? (
              error ? null : anyFilter ? (
                <EmptyState
                  icon={<InboxIcon />}
                  title="No entries match those filters"
                  action={
                    <Button variant="secondary" size="sm" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<InboxIcon />}
                  title="No notifications logged yet"
                />
              )
            ) : (
              <NotificationLogList
                rows={rows}
                loading={initialLoad}
                eventLabels={eventLabels}
                users={userMap}
              />
            )}
          </div>
        )}

        {!forbidden && result && total > 0 && (
          <ListPager
            page={page}
            totalPages={showAll ? 1 : totalPages}
            total={total}
            firstRow={firstRow}
            lastRow={lastRow}
            perPage={PER_PAGE}
            showAll={showAll}
            truncated={showAll && total > rows.length}
            loading={loading}
            noun={total === 1 ? 'entry' : 'entries'}
            onPage={(target) => applyPatch({ page: target <= 1 ? '' : String(target) })}
            onToggleShowAll={(next) => {
              setShowAll(next);
              if (next) applyPatch({ page: '' });
            }}
          />
        )}
      </Panel>
    </div>
  );
}
